import { createReducer, on } from '@ngrx/store';
import * as ProposalActions from './proposal.actions';
import { defaultProposalFilters } from './proposal.model';
import { IProposalState, initialUIState } from './proposal.state';

export const proposalReducer = createReducer(
  initialUIState,

  // Load proposals
  on(
    ProposalActions.loadProposalsForReview,
    (state): IProposalState => ({
      ...state,
      isLoading: true,
      error: null as string | null,
    }),
  ),

  on(
    ProposalActions.loadProposalsSuccess,
    (state, { response }): IProposalState => ({
      ...state,
      proposalsForReview: response.result || [],
      isLoading: false,
      error: null as string | null,
    }),
  ),

  on(
    ProposalActions.loadProposalsFailure,
    (state, { error }): IProposalState => ({
      ...state,
      isLoading: false,
      error: error as string | null,
    }),
  ),

  // Load proposals by creator
  on(
    ProposalActions.loadProposalsByCreator,
    (state): IProposalState => ({
      ...state,
      isLoading: true,
      error: null as string | null,
    }),
  ),

  on(
    ProposalActions.loadProposalsByCreatorSuccess,
    (state, { response, currentPage }): IProposalState => ({
      ...state,
      proposalByCreator: response.result?.items || [],
      totalCount: response.result?.meta?.count || 0,
      isLoading: false,
      error: null as string | null,
    }),
  ),

  on(
    ProposalActions.loadProposalsByCreatorFailure,
    (state, { error }): IProposalState => ({
      ...state,
      isLoading: false,
      error: error as string | null,
    }),
  ),

  // Filter actions
  on(ProposalActions.setFilter, (state, { filter }) => ({
    ...state,
    filters: {
      ...state.filters,
      ...filter,
    },
  })),

  on(ProposalActions.resetFilter, (state) => ({
    ...state,
    filters: { ...defaultProposalFilters },
    currentPage: 1,
  })),

  on(ProposalActions.updateFilter, (state, { key, value }) => ({
    ...state,
    filters: {
      ...state.filters,
      [key]: value,
    },
    currentPage: 1, // Reset to first page when filter changes
  })),

  // Pagination actions
  on(ProposalActions.updatePagination, (state, { page, pageSize }) => ({
    ...state,
    currentPage: page,
    filters: {
      ...state.filters,
      Page: page,
      PageSize: pageSize,
    },
  })),

  // Sorting actions
  on(ProposalActions.setSorting, (state, { sortBy, sortDescending }) => ({
    ...state,
    filters: {
      ...state.filters,
      sortBy,
      sortDescending,
    },
  })),

  // Loading state
  on(
    ProposalActions.setLoading,
    (state, { isLoading }): IProposalState => ({
      ...state,
      isLoading,
    }),
  ),

  // Load my proposals
  on(
    ProposalActions.loadMyProposals,
    (state): IProposalState => ({
      ...state,
      isLoadingMyProposals: true,
      error: null as string | null,
    }),
  ),

  on(
    ProposalActions.loadMyProposalsSuccess,
    (state, { response }): IProposalState => ({
      ...state,
      myProposals: response.result || [],
      isLoadingMyProposals: false,
      error: null as string | null,
    }),
  ),

  on(
    ProposalActions.loadMyProposalsFailure,
    (state, { error }): IProposalState => ({
      ...state,
      isLoadingMyProposals: false,
      error: error as string | null,
    }),
  ),

  // Accept proposal actions
  on(
    ProposalActions.acceptProposal,
    (state): IProposalState => ({
      ...state,
      isLoading: true,
      error: null as string | null,
    }),
  ),

  on(
    ProposalActions.acceptProposalSuccess,
    (state): IProposalState => ({
      ...state,
      isLoading: false,
      error: null as string | null,
    }),
  ),

  on(
    ProposalActions.acceptProposalFailure,
    (state, { error }): IProposalState => ({
      ...state,
      isLoading: false,
      error: error as string | null,
    }),
  ),

  // Decline proposal actions
  on(
    ProposalActions.declineProposal,
    (state): IProposalState => ({
      ...state,
      isLoading: true,
      error: null as string | null,
    }),
  ),

  on(
    ProposalActions.declineProposalSuccess,
    (state): IProposalState => ({
      ...state,
      isLoading: false,
      error: null as string | null,
    }),
  ),

  on(
    ProposalActions.declineProposalFailure,
    (state, { error }): IProposalState => ({
      ...state,
      isLoading: false,
      error: error as string | null,
    }),
  ),

  // Clear error
  on(
    ProposalActions.clearError,
    (state): IProposalState => ({
      ...state,
      error: null as string | null,
    }),
  ),

  // Load proposal by ID
  on(
    ProposalActions.loadProposalById,
    (state): IProposalState => ({
      ...state,
      isLoadingSelectedProposal: true,
      error: null as string | null,
    }),
  ),

  on(
    ProposalActions.loadProposalByIdSuccess,
    (state, { response }): IProposalState => ({
      ...state,
      selectedProposal: response.result || null,
      isLoadingSelectedProposal: false,
      error: null as string | null,
    }),
  ),

  on(
    ProposalActions.loadProposalByIdFailure,
    (state, { error }): IProposalState => ({
      ...state,
      selectedProposal: null,
      isLoadingSelectedProposal: false,
      error: error as string | null,
    }),
  ),

  // Get proposal document
  on(
    ProposalActions.getProposalDocument,
    (state): IProposalState => ({
      ...state,
      isLoadingDocument: true,
      error: null as string | null,
    }),
  ),

  on(
    ProposalActions.getProposalDocumentSuccess,
    (state, { document }): IProposalState => ({
      ...state,
      document,
      isLoadingDocument: false,
      error: null as string | null,
    }),
  ),

  on(
    ProposalActions.getProposalDocumentFailure,
    (state, { error }): IProposalState => ({
      ...state,
      isLoadingDocument: false,
      error: error as string | null,
    }),
  ),
);
